import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import AdvancedFilters from '../../components/admin/AdvancedFilters';
import api, { getAll } from '../../services/api';
import authStore from '../../store/authStore';
import { puoModificare } from '../../utils/permessi';
import notify from '../../utils/notifications';
import {
  Plus, Search, Eye, Edit, Trash2, CreditCard, Calendar, DollarSign,
  CheckCircle, XCircle, Clock, RefreshCw, X, FileText
} from 'lucide-react';

const Pagamenti = () => {
  const { utente } = authStore();
  const modificabile = puoModificare(utente);
  const navigate = useNavigate();
  const [pagamenti, setPagamenti] = useState([]);
  const [filteredPagamenti, setFilteredPagamenti] = useState([]);
  const [abbonamenti, setAbbonamenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPagamento, setEditingPagamento] = useState(null);
  const [formData, setFormData] = useState({
    abbonamento: '',
    importo: '',
    metodo: 'BONIFICO',
    stato: 'IN_ATTESA',
    note: '',
    riferimento_esterno: ''
  });

  useEffect(() => {
    caricaDati();
  }, []);

  const caricaDati = async () => {
    try {
      setLoading(true);
      
      const [pagamentiData, abbonamentiData] = await Promise.all([
        getAll('/pagamenti/pagamenti/'),
        getAll('/abbonamenti/abbonamenti/')
      ]);

      setPagamenti(pagamentiData);
      setFilteredPagamenti(pagamentiData);
      setAbbonamenti(abbonamentiData);
    } catch (error) {
      console.error('Errore caricamento dati:', error);
      notify.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = (filterValues) => {
    let filtered = [...pagamenti];

    // Filtro ricerca
    if (filterValues.search) {
      const searchLower = filterValues.search.toLowerCase();
      filtered = filtered.filter(pag =>
        pag.abbonamento_codice?.toLowerCase().includes(searchLower) ||
        pag.riferimento_esterno?.toLowerCase().includes(searchLower) ||
        pag.titolare_nome?.toLowerCase().includes(searchLower)
      );
    }

    // Filtro multi-metodo
    if (filterValues.metodi && filterValues.metodi.length > 0) {
      filtered = filtered.filter(pag => filterValues.metodi.includes(pag.metodo));
    }

    // Filtro multi-stato
    if (filterValues.stati && filterValues.stati.length > 0) {
      filtered = filtered.filter(pag => filterValues.stati.includes(pag.stato));
    }

    // Filtro range importo
    if (filterValues.importo_min) {
      filtered = filtered.filter(pag => parseFloat(pag.importo) >= parseFloat(filterValues.importo_min));
    }
    if (filterValues.importo_max) {
      filtered = filtered.filter(pag => parseFloat(pag.importo) <= parseFloat(filterValues.importo_max));
    }

    // Filtro range data pagamento
    if (filterValues.data_pagamento_inizio) {
      filtered = filtered.filter(pag => pag.data_pagamento >= filterValues.data_pagamento_inizio);
    }
    if (filterValues.data_pagamento_fine) {
      filtered = filtered.filter(pag => pag.data_pagamento <= filterValues.data_pagamento_fine);
    }

    setFilteredPagamenti(filtered);
  };

  const filterConfig = [
    {
      id: 'search',
      label: 'Cerca',
      type: 'text',
      placeholder: 'Codice abbonamento, riferimento, cliente...'
    },
    {
      id: 'stati',
      label: 'Stati',
      type: 'multi-select',
      options: [
        { value: 'COMPLETATO', label: 'Completato' },
        { value: 'IN_ATTESA', label: 'In Attesa' },
        { value: 'FALLITO', label: 'Fallito' },
        { value: 'RIMBORSATO', label: 'Rimborsato' }
      ]
    },
    {
      id: 'metodi',
      label: 'Metodi di Pagamento',
      type: 'multi-select',
      options: [
        { value: 'CONTANTI', label: 'Contanti' },
        { value: 'BONIFICO', label: 'Bonifico' },
        { value: 'CARTA', label: 'Carta' },
        { value: 'PAYPAL', label: 'PayPal' }
      ]
    },
    {
      id: 'importo',
      label: 'Importo (€)',
      type: 'number-range',
      step: 0.01
    },
    {
      id: 'data_pagamento',
      label: 'Data Pagamento',
      type: 'date-range'
    }
  ];

  const handleOpenModal = (pagamento = null) => {
    if (pagamento) {
      setEditingPagamento(pagamento);
      setFormData({
        abbonamento: pagamento.abbonamento,
        importo: pagamento.importo,
        metodo: pagamento.metodo,
        stato: pagamento.stato,
        note: pagamento.note || '',
        riferimento_esterno: pagamento.riferimento_esterno || ''
      });
    } else {
      setEditingPagamento(null);
      setFormData({
        abbonamento: '',
        importo: '',
        metodo: 'BONIFICO',
        stato: 'IN_ATTESA',
        note: '',
        riferimento_esterno: ''
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingPagamento(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const toastId = notify.loading(editingPagamento ? 'Aggiornamento...' : 'Creazione...');
    
    try {
      if (editingPagamento) {
        await api.put(`/pagamenti/pagamenti/${editingPagamento.id}/`, formData);
        notify.update(toastId, { type: 'success', message: 'Pagamento aggiornato con successo!' });
      } else {
        await api.post('/pagamenti/pagamenti/', formData);
        notify.update(toastId, { type: 'success', message: 'Pagamento creato con successo!' });
      }
      await caricaDati();
      handleCloseModal();
    } catch (error) {
      console.error('Errore salvataggio pagamento:', error);
      notify.update(toastId, { 
        type: 'error', 
        message: 'Errore nel salvataggio: ' + (error.response?.data?.detail || error.message)
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo pagamento?')) return;
    
    const toastId = notify.loading('Eliminazione...');
    
    try {
      await api.delete(`/pagamenti/pagamenti/${id}/`);
      notify.update(toastId, { type: 'success', message: 'Pagamento eliminato con successo!' });
      await caricaDati();
    } catch (error) {
      console.error('Errore eliminazione pagamento:', error);
      notify.update(toastId, { type: 'error', message: 'Errore nell\'eliminazione del pagamento' });
    }
  };

  const handleConferma = async (id) => {
    const toastId = notify.loading('Conferma in corso...');
    
    try {
      await api.patch(`/pagamenti/pagamenti/${id}/`, { 
        stato: 'COMPLETATO',
        data_conferma: new Date().toISOString()
      });
      notify.update(toastId, { type: 'success', message: 'Pagamento confermato!' });
      await caricaDati();
    } catch (error) {
      console.error('Errore conferma pagamento:', error);
      notify.update(toastId, { type: 'error', message: 'Errore nella conferma' });
    }
  };

  const getStatoBadge = (stato) => {
    switch (stato) {
      case 'COMPLETATO': return 'bg-green-100 text-green-800';
      case 'IN_ATTESA': return 'bg-yellow-100 text-yellow-800';
      case 'FALLITO': return 'bg-red-100 text-red-800';
      case 'RIMBORSATO': return 'bg-purple-100 text-purple-800';
      case 'CANCELLATO': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatoIcon = (stato) => {
    switch (stato) {
      case 'COMPLETATO': return <CheckCircle className="w-4 h-4" />;
      case 'IN_ATTESA': return <Clock className="w-4 h-4" />;
      case 'FALLITO': return <XCircle className="w-4 h-4" />;
      case 'RIMBORSATO': return <RefreshCw className="w-4 h-4" />;
      case 'CANCELLATO': return <X className="w-4 h-4" />;
      default: return <CreditCard className="w-4 h-4" />;
    }
  };

  const getMetodoIcon = (metodo) => {
    switch (metodo) {
      case 'CONTANTI': return '💵';
      case 'BONIFICO': return '🏦';
      case 'CARTA': return '💳';
      case 'PAYPAL': return '🅿️';
      case 'STRIPE': return '💠';
      default: return '💰';
    }
  };

  // Calcola statistiche
  const stats = {
    totale: pagamenti.reduce((sum, p) => sum + parseFloat(p.importo), 0),
    completati: pagamenti.filter(p => p.stato === 'COMPLETATO').length,
    inAttesa: pagamenti.filter(p => p.stato === 'IN_ATTESA').length,
    falliti: pagamenti.filter(p => p.stato === 'FALLITO').length,
    totaleCompletato: pagamenti
      .filter(p => p.stato === 'COMPLETATO')
      .reduce((sum, p) => sum + parseFloat(p.importo), 0),
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6">
        {/* Header */}
        <div className="flex flex-wrap justify-between items-center gap-3 mb-5">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Pagamenti</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Incassa le mensilità degli abbonamenti scolastici
            </p>
          </div>
          {modificabile && (
          <button
            onClick={() => handleOpenModal()}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#1b94ce] to-[#157bb0] rounded-lg text-white hover:opacity-90 transition shadow-sm font-semibold"
          >
            <Plus className="w-4 h-4" />
            Nuovo Pagamento
          </button>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
          <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <p className="text-gray-500 text-sm">Totale Incassato</p>
              <DollarSign className="w-4 h-4 text-green-500" />
            </div>
            <p className="text-2xl font-bold text-gray-900">€ {stats.totaleCompletato.toFixed(2)}</p>
            <p className="text-gray-400 text-xs mt-1">{stats.completati} pagamenti</p>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <p className="text-gray-500 text-sm">Totale</p>
              <CreditCard className="w-4 h-4 text-gray-400" />
            </div>
            <p className="text-2xl font-bold text-gray-900">{pagamenti.length}</p>
            <p className="text-gray-400 text-xs mt-1">pagamenti totali</p>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <p className="text-gray-500 text-sm">In Attesa</p>
              <Clock className="w-4 h-4 text-orange-500" />
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.inAttesa}</p>
            <p className="text-gray-400 text-xs mt-1">da confermare</p>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <p className="text-gray-500 text-sm">Falliti</p>
              <XCircle className="w-4 h-4 text-red-500" />
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.falliti}</p>
            <p className="text-gray-400 text-xs mt-1">non andati a buon fine</p>
          </div>
        </div>

        {/* Advanced Filters */}
        <AdvancedFilters
          filters={filterConfig}
          onApply={handleApplyFilters}
          resultCount={filteredPagamenti.length}
        />

        {/* Tabella Pagamenti */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {filteredPagamenti.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-24 h-24 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                <CreditCard className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Nessun pagamento trovato</h3>
              <p className="text-gray-600 mb-5">
                {filteredPagamenti.length !== pagamenti.length
                  ? 'Prova con altri criteri di ricerca' 
                  : 'Inizia registrando il primo pagamento'}
              </p>
              {filteredPagamenti.length === pagamenti.length && (
                <button
                  onClick={() => handleOpenModal()}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#1b94ce] to-[#157bb0] rounded-lg text-white hover:opacity-90 transition"
                >
                  <Plus className="w-4 h-4" />
                  Registra Pagamento
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Transazione
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Abbonamento
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Importo
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Metodo
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Stato
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Azioni
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredPagamenti.map((pag) => (
                    <tr key={pag.id} className="hover:bg-gray-50 transition">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <CreditCard className="w-4 h-4 text-blue-600" />
                          <div>
                            <div className="text-sm font-semibold text-gray-900">{pag.codice_transazione}</div>
                            {pag.riferimento_esterno && (
                              <div className="text-xs text-gray-500">Rif: {pag.riferimento_esterno.substring(0, 20)}...</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-900">{pag.abbonamento_codice || 'N/A'}</div>
                        <div className="text-xs text-gray-500">{pag.beneficiario_nome || ''}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm font-bold text-gray-900">€ {parseFloat(pag.importo).toFixed(2)}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <span className="text-xl">{getMetodoIcon(pag.metodo)}</span>
                          <span>{pag.metodo}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          {new Date(pag.data_pagamento).toLocaleDateString('it-IT')}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full font-semibold ${getStatoBadge(pag.stato)}`}>
                          {getStatoIcon(pag.stato)}
                          {pag.stato}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          {pag.stato === 'IN_ATTESA' && (
                            <button
                              onClick={() => handleConferma(pag.id)}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition"
                              title="Conferma pagamento"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => navigate(`/admin/pagamenti/${pag.id}`)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                            title="Visualizza dettagli"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleOpenModal(pag)}
                            className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition"
                            title="Modifica"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(pag.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                            title="Elimina"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Contatore */}
        {filteredPagamenti.length > 0 && (
          <div className="mt-4 text-sm text-gray-600 text-center">
            Totale: {filteredPagamenti.length} {filteredPagamenti.length === 1 ? 'pagamento' : 'pagamenti'} •
            Importo totale: € {filteredPagamenti.reduce((sum, p) => sum + parseFloat(p.importo), 0).toFixed(2)}
          </div>
        )}
      </div>

      {/* Modal Creazione/Modifica */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingPagamento ? 'Modifica Pagamento' : 'Nuovo Pagamento'}
              </h2>
              <button
                onClick={handleCloseModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Abbonamento *</label>
                <select
                  required
                  value={formData.abbonamento}
                  onChange={(e) => setFormData({ ...formData, abbonamento: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Seleziona abbonamento</option>
                  {abbonamenti.map((abb) => (
                    <option key={abb.id} value={abb.id}>
                      {abb.codice} - {abb.beneficiario_nome} (€ {parseFloat(abb.importo).toFixed(2)})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Importo (€) *</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={formData.importo}
                  onChange={(e) => setFormData({ ...formData, importo: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0.00"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Metodo Pagamento *</label>
                  <select
                    required
                    value={formData.metodo}
                    onChange={(e) => setFormData({ ...formData, metodo: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="CONTANTI">💵 Contanti</option>
                    <option value="BONIFICO">🏦 Bonifico Bancario</option>
                    <option value="CARTA">💳 Carta di Credito/Debito</option>
                    <option value="PAYPAL">🅿️ PayPal</option>
                    <option value="STRIPE">💠 Stripe</option>
                    <option value="ALTRO">💰 Altro</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Stato *</label>
                  <select
                    required
                    value={formData.stato}
                    onChange={(e) => setFormData({ ...formData, stato: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="IN_ATTESA">⏳ In Attesa</option>
                    <option value="COMPLETATO">✅ Completato</option>
                    <option value="FALLITO">❌ Fallito</option>
                    <option value="RIMBORSATO">🔄 Rimborsato</option>
                    <option value="CANCELLATO">🚫 Cancellato</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Riferimento Esterno</label>
                <input
                  type="text"
                  value={formData.riferimento_esterno}
                  onChange={(e) => setFormData({ ...formData, riferimento_esterno: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="ID Stripe, PayPal, numero bonifico, etc."
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Note</label>
                <textarea
                  value={formData.note}
                  onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Note aggiuntive sul pagamento..."
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-semibold"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-gradient-to-r from-[#1b94ce] to-[#157bb0] rounded-lg text-white hover:opacity-90 transition font-semibold"
                >
                  {editingPagamento ? 'Salva Modifiche' : 'Registra Pagamento'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default Pagamenti;
